import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack,
  FormControlLabel, Checkbox, Switch, Chip, Alert, CircularProgress, Divider, Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import { Link, useLocation } from "react-router-dom";

import { portalProfileApi, PP_ENDPOINTS } from "./api";
import { isSystemModuleActive, SYSTEM_MODULE_CODES } from "../../../../helpers/functions/systemModules";

const emptyProfile = {
  Name: "",
  Description: "",
  Priority: 10,
  FlagActive: 1,
  FlagCanComment: 0,
  FlagCanUploadDocument: 0,
  FlagCanViewFinancials: 0,
  FlagCanViewContractUsage: 0,
  fields: [],
};

/**
 * Portal Profiles admin — controls what a customer portal contact can SEE
 * (fields) and DO (action flags). Assigned to contacts on the customer screen.
 * Financials / contract usage are shown as distinct warning-styled toggles.
 */
// Change #100263 - the screen is both a Settings tab and the standalone
// /portal-profiles route. Standalone it needs page-content to clear the fixed
// top bar, or its first lines render underneath it; inside Settings that
// padding already exists and would be doubled.
const PageFrame = ({ children }) => {
  const { pathname } = useLocation();
  if (pathname.toLowerCase().startsWith("/portal-profiles")) {
    return <div className="page-content">{children}</div>;
  }
  return children;
};

const PortalProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // profile object or null
  const [saving, setSaving] = useState(false);
  const [branded, setBranded] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [list, fields, brand] = await Promise.all([
      portalProfileApi(PP_ENDPOINTS.list, "GET"),
      portalProfileApi(PP_ENDPOINTS.availableFields, "GET"),
      portalProfileApi(PP_ENDPOINTS.brandingGet, "GET"),
    ]);
    setLoading(false);
    if (list?.status === 200) setProfiles(list.portalprofile_list || []);
    else setError(list?.message || "Could not load profiles.");
    if (fields?.status === 200) setAvailableFields(fields.fields || []);
    if (brand?.status === 200) setBranded(!!brand.branded);
  }, []);

  const toggleBranded = async (value) => {
    setBranded(value);
    await portalProfileApi(PP_ENDPOINTS.brandingSet, "POST", { branded: value });
  };

  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ ...emptyProfile });
  const openEdit = (p) => setEditing({
    ...p,
    fields: (p.fields || []).map((f) => f.FieldKey),
  });

  const toggleField = (key) => {
    setEditing((prev) => {
      const has = prev.fields.includes(key);
      return { ...prev, fields: has ? prev.fields.filter((k) => k !== key) : [...prev.fields, key] };
    });
  };

  const save = async () => {
    setSaving(true);
    const body = {
      ...editing,
      fields: editing.fields.map((key, i) => ({ FieldKey: key, SortOrder: (i + 1) * 10 })),
    };
    const isUpdate = !!editing.Id;
    const res = await portalProfileApi(
      isUpdate ? PP_ENDPOINTS.update : PP_ENDPOINTS.create,
      isUpdate ? "PUT" : "POST",
      body
    );
    setSaving(false);
    if (res?.status === 200) {
      setEditing(null);
      load();
    } else {
      setError(res?.message || "Save failed.");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete portal profile "${p.Name}"?`)) return;
    const res = await portalProfileApi(PP_ENDPOINTS.del(p.Id), "DELETE");
    if (res?.status === 200) load();
    else setError(res?.message || "Delete failed.");
  };

  if (!isSystemModuleActive(SYSTEM_MODULE_CODES.CUSTOMER_JOBS_PORTAL)) {
    return (
      <PageFrame>
        <Box sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Customer Portal</Typography>
          <Alert severity="info">
            The <strong>Customer Jobs Portal</strong> module is turned off. Enable it in{" "}
            <Link to="/settings/5/1"><strong>Settings &rarr; Modules</strong></Link> to configure
            portal profiles and branding.
          </Alert>
        </Box>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Portal Profiles</Typography>
          <Typography variant="body2" color="text.secondary">
            Control what each customer portal contact can see and do. Assign profiles to contacts on the customer screen.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>New profile</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={branded} onChange={(e) => toggleBranded(e.target.checked)} />}
          label="Branded portal (show company / customer logo)"
        />
        <Typography variant="body2" color="text.secondary">
          When on, the portal shows the customer's own logo if one is set, otherwise your company logo. Turn off for an unbranded portal.
        </Typography>
      </Paper>

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Fields</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Capabilities</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profiles.map((p) => (
                <TableRow key={p.Id} hover>
                  <TableCell>{p.Name}</TableCell>
                  <TableCell>{p.Priority}</TableCell>
                  <TableCell>{(p.fields || []).length}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {p.FlagCanComment ? <Chip size="small" label="Comment" /> : null}
                      {p.FlagCanUploadDocument ? <Chip size="small" label="Upload" /> : null}
                      {p.FlagCanViewFinancials ? <Chip size="small" color="warning" label="Financials" /> : null}
                      {p.FlagCanViewContractUsage ? <Chip size="small" color="warning" label="Contract usage" /> : null}
                    </Stack>
                  </TableCell>
                  <TableCell>{Number(p.FlagActive) ? "Yes" : "No"}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(p)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => remove(p)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {profiles.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: "text.secondary" }}>No profiles yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* EDIT DIALOG */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editing?.Id ? "Edit profile" : "New profile"}</DialogTitle>
        <DialogContent dividers>
          {editing && (
            <Stack spacing={2}>
              <TextField label="Name" fullWidth value={editing.Name}
                onChange={(e) => setEditing({ ...editing, Name: e.target.value })} />
              <TextField label="Description" fullWidth value={editing.Description || ""}
                onChange={(e) => setEditing({ ...editing, Description: e.target.value })} />
              <TextField label="Priority (higher wins on conflict)" type="number" fullWidth value={editing.Priority}
                onChange={(e) => setEditing({ ...editing, Priority: Number(e.target.value) })} />
              <FormControlLabel
                control={<Switch checked={!!Number(editing.FlagActive)} onChange={(e) => setEditing({ ...editing, FlagActive: e.target.checked ? 1 : 0 })} />}
                label="Active"
              />

              <Divider />
              <Typography variant="subtitle2">Visible fields</Typography>
              <Box>
                {availableFields.map((f) => (
                  <FormControlLabel
                    key={f.key}
                    control={<Checkbox checked={editing.fields.includes(f.key)} onChange={() => toggleField(f.key)} />}
                    label={f.label}
                  />
                ))}
              </Box>

              <Divider />
              <Typography variant="subtitle2">Actions</Typography>
              <FormControlLabel
                control={<Checkbox checked={!!Number(editing.FlagCanComment)} onChange={(e) => setEditing({ ...editing, FlagCanComment: e.target.checked ? 1 : 0 })} />}
                label="Can add comments/notes"
              />
              <FormControlLabel
                control={<Checkbox checked={!!Number(editing.FlagCanUploadDocument)} onChange={(e) => setEditing({ ...editing, FlagCanUploadDocument: e.target.checked ? 1 : 0 })} />}
                label="Can upload documents"
              />

              <Divider />
              <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <WarningAmberIcon color="warning" fontSize="small" /> Sensitive data
              </Typography>
              <Tooltip title="Exposes outstanding balance and invoices to this contact">
                <FormControlLabel
                  control={<Checkbox color="warning" checked={!!Number(editing.FlagCanViewFinancials)} onChange={(e) => setEditing({ ...editing, FlagCanViewFinancials: e.target.checked ? 1 : 0 })} />}
                  label="Can view financials (balance + invoices)"
                />
              </Tooltip>
              <Tooltip title="Exposes contract item usage statements to this contact">
                <FormControlLabel
                  control={<Checkbox color="warning" checked={!!Number(editing.FlagCanViewContractUsage)} onChange={(e) => setEditing({ ...editing, FlagCanViewContractUsage: e.target.checked ? 1 : 0 })} />}
                  label="Can view contract usage"
                />
              </Tooltip>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !editing?.Name?.trim()}>
            {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PageFrame>
  );
};

export default PortalProfiles;
